import { Trans } from '@lingui/react/macro';
import { useEffect, useState } from 'react';
import { sanitizeDocxHtml, type DocxWorkerResponse } from '../../lib/docxPreview';

type DocxPreviewState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; html: string; warningCount: number };

export function DocxViewer({ bytes }: { bytes: Uint8Array }) {
  const [preview, setPreview] = useState<DocxPreviewState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    let stopped = false;
    const worker = new Worker(new URL('./docxPreview.worker.ts', import.meta.url), {
      type: 'module',
    });
    const stopWorker = () => {
      if (stopped) return;
      stopped = true;
      worker.terminate();
    };
    const timeout = window.setTimeout(() => {
      if (!active) return;
      active = false;
      stopWorker();
      setPreview({ status: 'error' });
    }, 15_000);

    setPreview({ status: 'loading' });
    worker.addEventListener('message', (event: MessageEvent<DocxWorkerResponse>) => {
      if (!active) return;
      active = false;
      window.clearTimeout(timeout);
      stopWorker();
      if (event.data.type === 'error') {
        setPreview({ status: 'error' });
        return;
      }
      setPreview({
        status: 'ready',
        html: sanitizeDocxHtml(event.data.html),
        warningCount: event.data.warningCount,
      });
    });
    worker.addEventListener('error', () => {
      if (!active) return;
      active = false;
      window.clearTimeout(timeout);
      stopWorker();
      setPreview({ status: 'error' });
    });

    const payload = new Uint8Array(bytes).buffer;
    worker.postMessage(payload, [payload]);

    return () => {
      active = false;
      window.clearTimeout(timeout);
      stopWorker();
    };
  }, [bytes]);

  if (preview.status === 'loading') {
    return <div className="file-viewer-loading"><Trans>Preparing document preview…</Trans></div>;
  }
  if (preview.status === 'error') {
    return <div className="file-viewer-error" role="alert"><Trans>This DOCX file is damaged or cannot be previewed</Trans></div>;
  }

  return (
    <div className="docx-viewer">
      <p className="docx-viewer-notice">
        <Trans>This is a simplified read-only preview. Layout may differ from Microsoft Word.</Trans>
      </p>
      {preview.warningCount > 0 && (
        <p className="docx-viewer-warning"><Trans>Some document content could not be displayed.</Trans></p>
      )}
      <div
        className="docx-viewer-content"
        role="document"
        dangerouslySetInnerHTML={{ __html: preview.html }}
      />
    </div>
  );
}
